import React, { useMemo } from 'react';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { Form, Radio } from 'formik-antd';
import { useDispatch } from 'react-redux';
import { Box, FormInput, Button, CustomInput } from 'atoms';
import { Debug } from '../Debug';
import { updateVendor } from 'store/vendorSlice';
import { ACL, EditModeBtn } from 'components/common';
import { useQuery } from 'hooks';
import { CancelButton } from 'utils/commonStyled';

const FormSchema = () =>
	Yup.object({
		related_party: Yup.boolean().required().label('Related Party'),
		black_listed: Yup.boolean().required().label('Black Listed'),
		holding_payment: Yup.boolean().required().label('Holding Payment'),
		logistic_vendor: Yup.boolean().required().label('Logistic vendor'),
		discount_early_payment: Yup.boolean().required().label('Discount Early Payment'),
		tds_applicable: Yup.boolean().required().label('TDS Applicable'),
		document_currency: Yup.string()
			.min(3)
			.max(500)
			.label('Document Currency')
			.matches(/^[a-zA-Z]*$/, 'Document Currency can only contain alphabets'),
		credit_period: Yup.number().min(1).max(999).label('credit period'),
		payment_tnc: Yup.string()
			.label('payment tnc')
			.min(2)
			.max(500)
			.matches(
				/^[a-zA-Z0-9 ,.-]*$/,
				'Payment TNC should only be alpha-Numeric, . (dot), - (hyphen) and , (commma) allowed'
			),
	});
export default function OtherDetailsForm({ isEditMode = false, formData = {}, otherFormsData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [query] = useQuery();

	const initialValues = useMemo(
		() => ({
			related_party: formData?.related_party || false,
			black_listed: formData?.black_listed || false,
			holding_payment: formData?.holding_payment || false,
			logistic_vendor: formData?.logistic_vendor || false,
			discount_early_payment: formData?.discount_early_payment || false,
			tds_applicable: formData?.tds_applicable || false,
			document_currency: formData?.document_currency || undefined,
			credit_period: formData?.credit_period || undefined,
			payment_tnc: formData?.payment_tnc || undefined,
		}),
		[formData]
	);

	const handleSubmit = (values, { setErrors, setSubmitting }) => {
		const payload = {
			...otherFormsData,
		};
		payload.other_info = values;

		if (isEditMode) {
			payload.update_type = 'vendor';
		} else {
			payload.create_type = 'vendor';
		}

		dispatch(updateVendor({ req_data: payload, info: 'other_info', setErrors, isEditMode }))
			.then(() => {
				setSubmitting(false);
				navigate('/vendor-list');
			})
			.catch(() => {
				setSubmitting(false);
			});
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{(values) => (
				<>
					{otherFormsData?.vendor_type === 'regular' && (
						<ACL action="update" module="vendor">
							{isEditMode && <EditModeBtn />}
						</ACL>
					)}

					<Form className={query.get('isEdit') === 'false' ? 'disabled-div' : ''}>
						<Box padding="1.5rem 2rem" background="#fff">
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="related_party" label="Related Party *">
										<Radio.Group
											name="related_party"
											defaultValue={values?.related_party}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="black_listed" label="Black Listed *">
										<Radio.Group
											name="black_listed"
											defaultValue={values?.black_listed}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="holding_payment" label="Holding Payment *">
										<Radio.Group
											name="holding_payment"
											defaultValue={values?.holding_payment}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="logistic_vendor" label="Logistic Vendor *">
										<Radio.Group
											name="logistic_vendor"
											defaultValue={values?.logistic_vendor}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput
										name="discount_early_payment"
										label="Discount Early Payment *"
									>
										<Radio.Group
											name="discount_early_payment"
											defaultValue={values?.discount_early_payment}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="tds_applicable" label="TDS Applicable *">
										<Radio.Group
											name="tds_applicable"
											defaultValue={values?.tds_applicable}
										>
											<Radio value>Yes</Radio>
											<Radio value={false}>No</Radio>
										</Radio.Group>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="document_currency" label="Document Currency">
										<CustomInput name="document_currency" />
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="credit_period" label="Credit Period">
										<CustomInput name="credit_period" />
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="payment_tnc" label="Payment tnc">
										<CustomInput name="payment_tnc" />
									</FormInput>
								</Col>
							</Row>
						</Box>

						{otherFormsData?.vendor_type === 'regular' && (
							<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
								<CancelButton
									onClick={() => navigate('/vendor-list')}
									type="button"
								>
									Cancel
								</CancelButton>
								<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
									{isEditMode ? 'Update' : 'Submit'}
								</Button>
							</Box>
						)}
						<Debug />
					</Form>
				</>
			)}
		</Formik>
	);
}

OtherDetailsForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	otherFormsData: PropTypes.object,
};
